/**
 * "View translation keys" mode.
 *
 * When enabled, the i18n loader swaps every resolved string for its full
 * dotted key (`common.save`, `navigation.dashboard`, …) instead of the
 * translated text. Admins flip this on from the language switcher to read a
 * key straight off any UI element and paste it into the Edit Localization
 * search. The real locale (`en`/`ar`) is untouched — only the messages change,
 * so text direction, dates, and routing stay intact.
 *
 * Driven by a cookie so it can be toggled client-side and read back in the
 * next-intl request config (`request.ts`) on the next server render.
 */

import { isMessageNamespace } from './namespaces';

export const KEYS_MODE_COOKIE = 'safha_i18n_keys';
export const KEYS_MODE_ON = '1';

/** True when the cookie value means keys mode is active. */
export function isKeysModeValue(value: string | null | undefined): boolean {
  return value === KEYS_MODE_ON;
}

/**
 * Deep-walk a loaded message tree (`{ namespace: { ...nested } }`) and replace
 * every leaf with its full dotted key path, i.e. the namespace plus the nested
 * path that addresses it. Object nesting is preserved so `t()` lookups resolve
 * to the same shape; only the leaf values change.
 *
 * Example: `{ common: { save: 'Save', dialog: { title: 'Hi' } } }`
 *       →  `{ common: { save: 'common.save', dialog: { title: 'common.dialog.title' } } }`
 *
 * Non-string leaves (numbers/booleans/arrays) and ICU/rich values collapse to
 * the key path too — in keys mode the literal key is exactly what we want to
 * show, and `t('x', {…})` simply renders the key string.
 */
export function toKeyIdentityMessages(
  messages: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const namespace of Object.keys(messages)) {
    result[namespace] = mapNode(messages[namespace], namespace);
  }
  return result;
}

function mapNode(node: unknown, path: string): unknown {
  if (node !== null && typeof node === 'object' && !Array.isArray(node)) {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(node as Record<string, unknown>)) {
      out[key] = mapNode(
        (node as Record<string, unknown>)[key],
        `${path}.${key}`
      );
    }
    return out;
  }
  // Leaf — its searchable identity is its full dotted key path.
  return path;
}

/**
 * A dotted token that *could* be a translation key: a leading letter, then one
 * or more dot-separated segments. Validated against the real namespace list
 * below, which is what keeps `admin@gmail.com` and `logo.png` out.
 */
const KEY_TOKEN = /[A-Za-z][A-Za-z0-9_-]*(?:\.[A-Za-z0-9_-]+)+/g;

/** Characters that, immediately before a token, mean it isn't a standalone key. */
const GLUED_BEFORE = /[A-Za-z0-9_@]/;

export type TranslationKeyMatch = {
  key: string;
  /** Offsets within `text`, so the caller can build a Range over the token. */
  start: number;
  end: number;
};

/**
 * Find the translation key the cursor sits inside, given the text of a single
 * text node and a character offset into it.
 *
 * Keys mode puts bare dotted keys straight into the DOM, so the hover inspector
 * has nothing to attach to but the text itself — it hit-tests a caret position
 * and asks this function what key, if any, is under it. Returns `null` when the
 * offset isn't inside a token whose first segment is a loaded namespace.
 */
export function findTranslationKeyAt(
  text: string,
  offset: number
): TranslationKeyMatch | null {
  KEY_TOKEN.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = KEY_TOKEN.exec(text)) !== null) {
    const token = match[0];
    const start = match.index;
    const end = start + token.length;

    if (offset < start || offset > end) continue;
    if (start > 0 && GLUED_BEFORE.test(text[start - 1])) return null;
    if (!isMessageNamespace(token.slice(0, token.indexOf('.')))) return null;

    return { key: token, start, end };
  }

  return null;
}
